import React, { useState, useMemo, useRef, createContext, useContext, useEffect } from 'react';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import { CssBaseline, Container, Box, AppBar, Toolbar, Typography } from '@mui/material';
import { BrowserRouter as Router, Routes, Route, Link, useLocation, useNavigate } from 'react-router-dom';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import HomeOutlinedIcon from '@mui/icons-material/HomeOutlined';
import MemoryIcon from '@mui/icons-material/Memory';
import ListAltIcon from '@mui/icons-material/ListAlt';
import SettingsIcon from '@mui/icons-material/Settings';
import InfoIcon from '@mui/icons-material/Info';
import Home from './components/Home';
import Memorize from './components/Memorize';
import Plans from './components/Plans';
import PlanView from './components/PlanView';
import Progress from './components/Progress';
import Settings from './components/Settings';
import About from './components/About';
import ErrorBoundary from './components/ErrorBoundary';
import { getSettings } from './utils/storage';

// Theme Context
const ThemeContext = createContext();

export const useThemeContext = () => useContext(ThemeContext);

// Navigation Component (replaces tabs)
const Navigation = ({ theme }) => {
  const location = useLocation();
  const navigate = useNavigate();

  const navItems = [
    { to: '/', icon: HomeOutlinedIcon, label: 'Home' },
    { to: '/memorize', icon: MemoryIcon, label: 'Memorize' },
    { to: '/plans', icon: ListAltIcon, label: 'Plans' },
    { to: '/progress', icon: MenuBookIcon, label: 'Verses' },
    { to: '/settings', icon: SettingsIcon, label: 'Settings' },
    { to: '/about', icon: InfoIcon, label: 'About' },
  ];

  const swipeThreshold = 50;
  const touchStartRef = useRef(null);

  const handleTouchStart = (e) => {
    touchStartRef.current = e.touches[0].clientX;
  };

  const handleTouchEnd = (e) => {
    if (!touchStartRef.current) return;

    const touchEnd = e.changedTouches[0].clientX;
    const deltaX = touchEnd - touchStartRef.current;

    if (Math.abs(deltaX) > swipeThreshold) {
      const currentIndex = navItems.findIndex(item => item.to === location.pathname);
      let newIndex;

      if (deltaX > 0) {
        // Swipe right (previous tab)
        newIndex = Math.max(currentIndex - 1, 0);
      } else {
        // Swipe left (next tab)
        newIndex = Math.min(currentIndex + 1, navItems.length - 1);
      }

      if (newIndex !== currentIndex) {
        navigate(navItems[newIndex].to);
      }
    }

    touchStartRef.current = null;
  };

  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'center',
        gap: {xs: 0.5, sm: 1},
        pb: 2,
        mt: 2,
        pl: {xs: 1, sm: 2}, // Add padding to prevent cut-off
        pr: {xs: 1, sm: 2},
        borderBottom: 1,
        borderColor: 'outline.light',
        overflowX: {xs: 'hidden', sm: 'auto'}, // Hide horizontal scrolling on mobile for swipe
        scrollbarWidth: {xs: 'none', sm: 'none'}, // Hide scrollbar Firefox
        '&::-webkit-scrollbar': { display: {xs: 'none', sm: 'none'} }, // Hide scrollbar Webkit
      }}
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
    >
      {navItems.map((item) => {
        const isActive = location.pathname === item.to;
        return (
          <Box key={item.to} component={Link} to={item.to} sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            px: {xs: 1, sm: 2, md: 3},
            py: 0.5,
            borderRadius: '25px',
            backgroundColor: isActive ? 'primary.main' : 'transparent',
            color: isActive ? 'primary.contrastText' : 'text.secondary',
            textDecoration: 'none',
            transition: 'all 0.2s ease',
            flexShrink: 0, // Prevent shrinking
            '&:hover': {
              backgroundColor: isActive ? 'primary.dark' : 'action.hover',
            },
          }}>
            <item.icon sx={{ fontSize: {xs: 16, sm: 18} }} />
            <Typography variant="button" sx={{ fontSize: {xs: '0.75rem', sm: '0.875rem'} }}>
              {item.label}
            </Typography>
          </Box>
        );
      })}
    </Box>
  );
};

// Material Design 3 Color System
const md3LightTheme = createTheme({
  palette: {
    mode: 'light',
    primary: {
      main: '#4285F4', // Google Brand Blue
      light: '#BBDEFB',
      dark: '#1565C0',
      contrastText: '#FFFFFF',
    },
    secondary: {
      main: '#625B71', // MD3 Secondary
      light: '#E8DEF8',
      dark: '#4A4458',
      contrastText: '#FFFFFF',
    },
    tertiary: {
      main: '#7D5260', // MD3 Tertiary
      light: '#EFB8C8',
      dark: '#633B48',
      contrastText: '#FFFFFF',
    },
    error: {
      main: '#B3261E', // MD3 Error
      light: '#F9BDBB',
      dark: '#9C2A2E',
      contrastText: '#FFFFFF',
    },
    warning: {
      main: '#F9A825', // MD3 Warning
      light: '#FFF8E1',
      dark: '#C49000',
      contrastText: '#000000',
    },
    info: {
      main: '#0288D1', // MD3 Info
      light: '#B3E5FC',
      dark: '#01579B',
      contrastText: '#FFFFFF',
    },
    success: {
      main: '#4CAF50', // MD3 Success
      light: '#E8F5E8',
      dark: '#2E7D32',
      contrastText: '#FFFFFF',
    },
    background: {
      default: '#FFFBFE', // MD3 Surface
      paper: '#FFFBFE',
      surfaceVariant: '#F7F2FA',
      surfaceContainerHighest: '#E7E0EC',
    },
    surface: {
      main: '#FFFBFE',
      variant: '#F7F2FA',
      containerHighest: '#E7E0EC',
    },
    outline: {
      main: '#79747E',
      variant: '#CAC4D0',
      light: '#E7E0EC',
    },
    text: {
      primary: '#1D192B',
      secondary: '#49454F',
      disabled: '#79747E',
      inversePrimary: '#FFFFFF',
      inverseSecondary: '#E6E0E9',
      inverseDisabled: '#CAC4D0',
    },
  },
  typography: {
    // Material Design 3 Typography Scale
    fontFamily: '"Roboto Flex", "Roboto", "Helvetica Neue", Arial, sans-serif',
    h1: {
      fontSize: '3.5rem', // 57px
      fontWeight: 400,
      lineHeight: 1.12,
      letterSpacing: '-0.25px',
    },
    h2: {
      fontSize: '2.25rem', // 36px
      fontWeight: 400,
      lineHeight: 1.2,
      letterSpacing: '0px',
    },
    h3: {
      fontSize: '1.875rem', // 30px
      fontWeight: 500,
      lineHeight: 1.33,
      letterSpacing: '0px',
    },
    h4: {
      fontSize: '1.5rem', // 24px
      fontWeight: 500,
      lineHeight: 1.33,
      letterSpacing: '0.15px',
    },
    h5: {
      fontSize: '1.25rem', // 20px
      fontWeight: 500,
      lineHeight: 1.4,
      letterSpacing: '0.15px',
    },
    h6: {
      fontSize: '1rem', // 16px
      fontWeight: 600,
      lineHeight: 1.5,
      letterSpacing: '0.15px',
    },
    subtitle1: {
      fontSize: '1rem', // 16px
      fontWeight: 500,
      lineHeight: 1.5,
      letterSpacing: '0.15px',
    },
    subtitle2: {
      fontSize: '0.875rem', // 14px
      fontWeight: 600,
      lineHeight: 1.43,
      letterSpacing: '0.1px',
    },
    body1: {
      fontSize: '1rem', // 16px
      fontWeight: 400,
      lineHeight: 1.5,
      letterSpacing: '0.5px',
    },
    body2: {
      fontSize: '0.875rem', // 14px
      fontWeight: 400,
      lineHeight: 1.43,
      letterSpacing: '0.25px',
    },
    caption: {
      fontSize: '0.75rem', // 12px
      fontWeight: 400,
      lineHeight: 1.33,
      letterSpacing: '0.4px',
    },
    overline: {
      fontSize: '0.75rem', // 12px
      fontWeight: 600,
      lineHeight: 1.33,
      letterSpacing: '1.5px',
      textTransform: 'uppercase',
    },
    button: {
      fontSize: '0.875rem', // 14px
      fontWeight: 600,
      lineHeight: 1.43,
      letterSpacing: '0.5px',
      textTransform: 'none',
    },
  },
  shape: {
    // Material Design 3 border radius
    borderRadius: 10, // Consistent border radius
  },
  spacing: 8, // MD3 Base spacing unit
  shadows: [
    'none',
    '0px 2px 4px rgba(0, 0, 0, 0.1)',
    '0px 4px 8px rgba(0, 0, 0, 0.1)',
    '0px 8px 16px rgba(0, 0, 0, 0.1)',
    '0px 16px 32px rgba(0, 0, 0, 0.1)',
    ...Array(19).fill('0px 24px 48px rgba(0, 0, 0, 0.15)'),
  ],
  components: {
    // Material Design 3 Card Styling
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: '10px',
          background: 'linear-gradient(145deg, rgba(255, 251, 254, 0.9) 0%, rgba(247, 242, 250, 0.8) 100%)',
          backdropFilter: 'blur(20px)',
          border: '1px solid rgba(125, 82, 96, 0.08)',
          boxShadow: '0px 4px 16px rgba(0, 0, 0, 0.08)',
          transition: 'all 0.3s cubic-bezier(0.2, 0, 0, 1)',
          '&:hover': {
            boxShadow: '0px 8px 32px rgba(0, 0, 0, 0.12)',
            transform: 'translateY(-2px)',
          },
        },
        media: {
          borderTopLeftRadius: '10px',
          borderTopRightRadius: '10px',
        },
      },
    },
    // Material Design 3 Button Styling
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: '10px',
          padding: '12px 24px',
          fontSize: '0.875rem',
          fontWeight: 600,
          textTransform: 'none',
          boxShadow: 'none',
          '&:hover': {
            boxShadow: '0px 4px 8px rgba(0, 0, 0, 0.12)',
          },
        },
        contained: {
          background: 'linear-gradient(145deg, #4285F4 0%, #1565C0 100%)',
          '&:hover': {
            background: 'linear-gradient(145deg, #63A4FF 0%, #0D47A1 100%)',
          },
        },
        outlined: {
          borderColor: '#79747E',
          borderWidth: '1px',
          '&:hover': {
            backgroundColor: 'rgba(66, 133, 244, 0.04)',
            borderColor: '#4285F4',
          },
        },
      },
    },
    // Material Design 3 Tab Styling
    MuiTab: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontSize: '0.875rem',
          fontWeight: 600,
          letterSpacing: '0.5px',
          borderRadius: '16px 16px 0 0',
          marginRight: '4px',
          '&.Mui-selected': {
            backgroundColor: 'rgba(255, 255, 255, 0.12)',
            borderRadius: '16px 16px 0 0',
          },
        },
      },
    },
    // Material Design 3 AppBar Styling
    MuiAppBar: {
      styleOverrides: {
        root: {
          background: 'linear-gradient(135deg, rgba(255, 251, 254, 0.95) 0%, rgba(247, 242, 250, 0.9) 100%)',
          backdropFilter: 'blur(20px)',
          borderBottom: '1px solid rgba(125, 82, 96, 0.12)',
          boxShadow: '0px 1px 3px rgba(0, 0, 0, 0.1)',
          color: '#1D192B',
        },
      },
    },
    // Material Design 3 Paper Styling
    MuiPaper: {
      styleOverrides: {
        root: {
          borderRadius: '10px !important',
          background: 'linear-gradient(145deg, #FFFBFE 0%, #F7F2FA 100%)',
          border: '1px solid rgba(125, 82, 96, 0.08)',
        },
      },
    },
    // Material Design 3 Input Styling
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: '10px',
            '&:hover .MuiOutlinedInput-notchedOutline': {
              borderColor: '#4285F4',
              borderWidth: '2px',
            },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
              borderColor: '#4285F4',
              borderWidth: '2px',
            },
          },
        },
      },
    },
    // Material Design 3 Chip Styling
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: '10px',
          fontWeight: 600,
        },
      },
    },
  },
});

const md3DarkTheme = createTheme({
  palette: {
    mode: 'dark',
    primary: {
      main: '#8AB4F8', // Google Blue for Dark Mode
      light: '#BBDEFB',
      dark: '#1565C0',
      contrastText: '#1A1C19',
    },
    secondary: {
      main: '#E0E0E0', // Light gray for better contrast
      light: '#F5F5F5',
      dark: '#B0B0B0',
      contrastText: '#1A1C19',
    },
    tertiary: {
      main: '#81C784', // Subdued green for dark mode
      light: '#A5D6A7',
      dark: '#4CAF50',
      contrastText: '#1A1C19',
    },
    background: {
      default: '#1C1B1F', // MD3 Dark Surface
      paper: '#1C1B1F',
      surfaceVariant: '#49454F',
      surfaceContainerHighest: '#36343B',
    },
    surface: {
      main: '#1C1B1F',
      variant: '#49454F',
      containerHighest: '#36343B',
    },
    outline: {
      main: '#938F99',
      variant: '#CAC4D0',
      light: '#CAC4D0',
    },
    text: {
      primary: '#E6E0E9',
      secondary: '#CAC4D0',
      disabled: '#938F99',
      inversePrimary: '#1C1B1F',
      inverseSecondary: '#49454F',
      inverseDisabled: '#36343B',
    },
  },
});

function App() {
  const [isDarkMode, setIsDarkMode] = useState(() => {
    const settings = getSettings();
    return settings.themeMode === 'dark' || (settings.themeMode === 'auto' && window.matchMedia('(prefers-color-scheme: dark)').matches);
  });

  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    const handleChange = (e) => {
      const settings = getSettings();
      if (settings.themeMode === 'auto') {
        setIsDarkMode(e.matches);
      }
    };

    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, []);

  const theme = useMemo(() =>
    createTheme(isDarkMode ? md3DarkTheme : md3LightTheme),
    [isDarkMode]
  );

  const contextValue = {
    isDarkMode,
    setIsDarkMode,
  };

  return (
    <ThemeContext.Provider value={contextValue}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <Router>
        <Box sx={{
          minHeight: '100vh',
          background: isDarkMode
            ? 'linear-gradient(135deg, #1C1B1F 0%, #2D2926 100%)'
            : 'linear-gradient(135deg, #FFFBFE 0%, #F7F2FA 100%)',
          transition: 'background 0.3s ease',
        }}>
          {/* Material Design 3 App Header */}
          <AppBar
            position="static"
            elevation={0}
            sx={{
              background: 'transparent',
              backdropFilter: 'blur(20px)',
            }}
          >
            <Toolbar>
              <MenuBookIcon
                sx={{
                  mr: 2,
                  fontSize: 32,
                  color: 'primary.main',
                  transition: 'color 0.3s ease',
                }}
              />
              <Typography variant="h5" component="div" sx={{ flexGrow: 1, color: 'text.primary' }}>
                Bible Memory
              </Typography>
            </Toolbar>
          </AppBar>

          {/* Main Content */}
          <Container maxWidth="lg" sx={{ pt: 2, pb: 4 }}>
            <Box
              sx={{
                backgroundColor: 'background.paper',
                borderRadius: '10px',
                boxShadow: theme.shadows[2],
                overflow: 'hidden',
                border: '1px solid',
                borderColor: 'outline.light',
              }}
            >
              {/* Navigation */}
              <Navigation theme={theme} />

              {/* Routes */}
              <ErrorBoundary>
                <Box sx={{ p: 3, minHeight: '60vh' }}>
                  <Routes>
                    <Route path="/" element={
                      <ErrorBoundary>
                        <Home />
                      </ErrorBoundary>
                    } />
                    <Route path="/memorize" element={
                      <ErrorBoundary>
                        <Memorize />
                      </ErrorBoundary>
                    } />
                    <Route path="/plans" element={
                      <ErrorBoundary>
                        <Plans />
                      </ErrorBoundary>
                    } />
                    <Route path="/plan/:planId" element={
                      <ErrorBoundary>
                        <PlanView />
                      </ErrorBoundary>
                    } />
                    <Route path="/progress" element={
                      <ErrorBoundary>
                        <Progress />
                      </ErrorBoundary>
                    } />
                    <Route path="/about" element={
                      <ErrorBoundary>
                        <About />
                      </ErrorBoundary>
                    } />
                    <Route path="/settings" element={
                      <ErrorBoundary>
                        <Settings />
                      </ErrorBoundary>
                    } />
                  </Routes>
                </Box>
              </ErrorBoundary>
            </Box>
          </Container>
        </Box>
      </Router>
    </ThemeProvider>
  </ThemeContext.Provider>
  );
}

export default App;
